// Angular imports
import { Component, OnInit, OnDestroy } from '@angular/core';

// Import services

// Import types
import { CalculationHeaderApi, CalculationLineApi, CalculationMetaApi, CalculationTailApi } from '../../../types/calculation';
import { BehaviorSubject, Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state-management/app-state';
import * as CalculationActions from 'src/app/state-management/calculation-actions';
import { selectCalculationMeta, selectCurrentSelection, selectHourRates } from 'src/app/state-management/calculation-selectors';
import { CalculationSelection, ViewModelLine, isCalculationLineApi } from 'src/app/state-management/calculation-state';

@Component({
    selector: 'hourly-rate-dropdown',
    templateUrl: './hourly-rate-dropdown.component.html',
    styleUrls: ['./hourly-rate-dropdown.component.scss'],
    standalone: false
})
export class HourlyRateDropdownComponent implements OnInit, OnDestroy {
    /**
     * Used to unsubscribe all listeners onDestroy
     *
     * @private
     * @memberof HourlyRateDropdownComponent
     */
    private unsubscribe$ = new Subject<void>();

    public hourRatesSubject$ = new BehaviorSubject<CalculationHeaderApi[]>([]);
    public hourRates$ = this.hourRatesSubject$.asObservable();

    public selection$: Observable<CalculationSelection>;
    public selectedLine$ = new BehaviorSubject<(CalculationLineApi | CalculationTailApi)>(undefined);
    public metaSubject$ = new BehaviorSubject<CalculationMetaApi>(undefined);

    public _selectedIndex: number = 0;
    public _open: boolean = false;
    public _active: boolean = false;

    constructor(
        private store: Store<AppState>
    ) {
    }

    public ngOnInit() {
        this.selection$ = this.store.select(selectCurrentSelection).pipe(
            takeUntil(this.unsubscribe$)
        )

        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.metaSubject$.next(meta);
        })

        combineLatest([
            this.store.select(selectHourRates),
            this.selection$
        ]).pipe(
            takeUntil(this.unsubscribe$),
        ).subscribe(([hourRates, selection]) => {
            if (hourRates !== undefined) {
                this.hourRatesSubject$.next(hourRates);
            }

            // Idle if no selection or no calculation line
            if (!selection || (selection.selectedLines?.length !== 1) || !isCalculationLineApi(selection.selectedLines[0]) || selection.selectedLines[0].isTextLine) {
                // Set inactive and close
                this._active = false;
                this._open = false;
            } else {
                this._active = true;
                const selectedLine = selection.selectedLines[0];

                this.selectedLine$.next(selectedLine);

                // Find the selected index
                const firstSelected = selection.selectedLines[0];
                if (isCalculationLineApi(firstSelected)) {
                    this._selectedIndex = hourRates.findIndex(rate => rate.id === (firstSelected as CalculationLineApi).rate);
                }

                // console.log('selected index', this._selectedIndex, selectedLine.rate, hourRates)
            }
        })
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /* --- Event handlers --- */

    public _toggleOpen() {
        // Make sure dropdown is active
        if (!this._active) return;

        // Toggle open
        this._open = !this._open;
    }

    public _handleClickHourlyRate(index: number) {
        // If the selection doesn't change or is invalid, simply close
        if (this._selectedIndex === -1 || index === this._selectedIndex || !this.metaSubject$.value || !this.selectedLine$.value) {
            this._open = false;
            return;
        }

        // // Get the selected line
        const selectedLine = this.selectedLine$.value;

        if (selectedLine !== undefined && isCalculationLineApi(selectedLine) && !selectedLine.isTextLine) {
            // Close
            this._open = false;

            // Get the hourly rate
            const rate = this.hourRatesSubject$.value[index];

            // Assign the hourly rate
            const copySelectedLine: CalculationLineApi = {
                ...selectedLine,
                rate: rate.id
            }

            // Set selected index
            this._selectedIndex = index;

            // Update remote
            this.store.dispatch(CalculationActions.updateLineUi({
                projectId: this.metaSubject$.value.projectId,
                calculationId: this.metaSubject$.value.id,
                line: copySelectedLine
            }))
        }
        // this.dataService.updateLine(this.applicationDataService.activeProject, this.applicationDataService.activeCalculation, selectedLine);
    }
}
